// The details panel's write state — the state half of `<MachinePanel/>`'s Actions
// (#6563), split out the way `lib/artifact-actions.ts` is: four
// mutations, a two-step confirm and a failure notice are STATE, not rendering,
// and this unit-tests without a shell.
//
// Four rules:
//
//   - **One write at a time, and the refusal knows WHICH.** The panel holds
//     three independent controls, so a single error slot would paint an
//     ownership refusal inside the briefing form. `error()` carries the write it
//     belongs to and each control reads `errorFor(its kind)` — the two-error
//     split `createAgentConfigEditor` makes, generalized to three.
//   - **Every write RE-READS.** `onWritten` refetches the point read and the
//     roster behind it; nothing patches a row from a write's echo. The prompt
//     write's echo is a 120-char preview and the owner write's is the pair we
//     just sent, so neither is the row.
//   - **A drain reports what it DID.** The route answers `flipped_offline` and
//     the tasks it re-queued; "already offline" and "evacuated 3 tasks" are
//     different outcomes and read as themselves. Same rule as the issue actions'
//     "a 202 is not a success". An undrain likewise says whether it CLEARED a
//     mark, and is one click: it only lets a beating box back in, nothing is lost.
//   - **Clearing a briefing is its own act.** `clearPrompt` sends `null`
//     deliberately; nothing infers a clear from an empty textarea, because
//     "I emptied the box" and "I meant to wipe the briefing" are two intents.

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { errorReason } from './api';
import {
  type BoxOwnership,
  drainBox,
  setBoxOwner,
  setBoxPrompt,
  undrainBox,
} from './box-lifecycle';
import { drainNotice, undrainNotice } from './machine-drain';

/** Which of the panel's writes a refusal or a pending call belongs to. */
export type BoxWrite = 'owner' | 'prompt' | 'drain' | 'undrain';

export type BoxActions = {
  /** The write in flight, or null. */
  readonly pending: () => BoxWrite | null;
  /** The API's refusal for ONE control, rendered beside it. */
  readonly errorFor: (kind: BoxWrite) => string | null;
  /** What the last write did, in the server's terms. Cleared when the next one starts. */
  readonly notice: () => string | null;
  /** True while the drain is one click from firing (inline two-step confirm). */
  readonly confirmingDrain: () => boolean;
  readonly askDrain: () => void;
  readonly cancelDrain: () => void;
  /** Drain the box under confirmation. Dropped while any write is in flight. */
  readonly confirmDrain: () => void;
  /** Clear a drain mark. Unconfirmed: it holds nothing back that a confirm protects. */
  readonly undrain: () => void;
  readonly saveOwner: (ownership: BoxOwnership) => void;
  /** Replace the briefing with this prose. Blank is refused here — clearing is `clearPrompt`. */
  readonly savePrompt: (prompt: string) => void;
  readonly clearPrompt: () => void;
  /** Drop every notice, refusal and confirm — the panel opening another machine. */
  readonly reset: () => void;
};

/**
 * Write state for one details panel. `boxId` is an accessor because the panel
 * re-targets without remounting; a write against a stale id is impossible
 * because every call reads it, and the name, at fire time (`run`).
 */
export function createBoxActions(input: {
  readonly boxId: () => string | null;
  /** The machine's name, which a drain's notice says (`drainNotice`). */
  readonly name: () => string;
  readonly token: string | null;
  readonly onWritten: () => void;
}): BoxActions {
  const [pending, setPending] = createSignal<BoxWrite | null>(null);
  const [error, setError] = createSignal<{ kind: BoxWrite; message: string } | null>(null);
  const [notice, setNotice] = createSignal<string | null>(null);
  const [confirmingDrain, setConfirmingDrain] = createSignal(false);

  /**
   * One in-flight write for the whole panel, with the guard released either way.
   *
   * THE TARGET IS CAPTURED AT FIRE TIME, id and name together. The panel re-targets
   * without remounting, so a write fired on A can land while B is selected: reading
   * `input.name()` after the await would report B as stopped or started when only A
   * changed. What a write says (its notice, its refusal, closing its confirm) is shown only
   * while its machine is still the one selected; the re-read runs either way, since the
   * write happened.
   */
  const run = (
    kind: BoxWrite,
    call: (target: { readonly id: string; readonly name: string }) => Promise<string>,
  ): void => {
    const id = input.boxId();
    if (id === null || pending() !== null) return;
    const target = { id, name: input.name() };
    const stillSelected = (): boolean => input.boxId() === target.id;
    setPending(kind);
    setError(null);
    setNotice(null);
    void call(target)
      .then((message) => {
        if (stillSelected()) {
          if (kind === 'drain') setConfirmingDrain(false);
          setNotice(message);
        }
        input.onWritten();
      })
      .catch((e: unknown) => {
        if (stillSelected()) setError({ kind, message: errorReason(e, t('fleet.panel.failed')) });
      })
      .finally(() => setPending(null));
  };

  const confirmDrain = (): void =>
    run('drain', async (target) =>
      drainNotice(target.name, await drainBox(target.id, input.token)),
    );

  return {
    pending,
    errorFor: (kind) => {
      const current = error();
      return current !== null && current.kind === kind ? current.message : null;
    },
    notice,
    confirmingDrain,
    askDrain: () => {
      setError(null);
      setConfirmingDrain(true);
    },
    cancelDrain: () => setConfirmingDrain(false),
    confirmDrain,
    undrain: () =>
      run('undrain', async (target) =>
        undrainNotice(target.name, await undrainBox(target.id, input.token)),
      ),
    saveOwner: (ownership) =>
      run('owner', async (target) => {
        await setBoxOwner(target.id, ownership, input.token);
        return t('fleet.panel.owner_saved');
      }),
    savePrompt: (prompt) => {
      // A blank box never reaches the wire: the API reads `''` as a CLEAR, and
      // wiping a briefing by leaving a form empty is the one thing this panel
      // must not do by accident. `clearPrompt` is the deliberate door.
      if (prompt.trim() === '') return;
      run('prompt', async (target) => {
        await setBoxPrompt(target.id, prompt.trim(), input.token);
        return t('fleet.panel.prompt_saved');
      });
    },
    clearPrompt: () =>
      run('prompt', async (target) => {
        await setBoxPrompt(target.id, null, input.token);
        return t('fleet.panel.prompt_cleared');
      }),
    reset: () => {
      setError(null);
      setNotice(null);
      setConfirmingDrain(false);
    },
  };
}
